/**
 * Where the notifications bridge sends what the stream says. It holds no state: every input
 * goes to each resource's stream handler, and each handler decides whether it concerns it.
 *
 * A resource's handler is `<resource>StreamAtom` in `src/state/api/<resource>Atoms.ts`, beside
 * the atoms it writes — so this list names every resource the stream feeds, and the name leads
 * to what the stream does to it.
 */

import { atom, type WritableAtom } from 'jotai';

import type { StreamInput } from '@/types/notifications';

import { agentSettingsStreamAtom } from './api/agentSettingsAtoms';
import { binaryFilesStreamAtom } from './api/binaryFilesAtoms';
import { devicesStreamAtom } from './api/devicesAtoms';
import { healthMessagesStreamAtom } from './api/healthMessagesAtoms';
import { liveCamerasStreamAtom } from './api/liveCamerasAtoms';
import { liveStatusStreamAtom } from './api/liveStatusAtoms';
import { locationsStreamAtom } from './api/locationsAtoms';
import { meStreamAtom } from './api/meAtoms';
import { rulesStreamAtom } from './api/rulesAtoms';
import { serversStreamAtom } from './api/serversAtoms';
import { serverSettingsStreamAtom } from './api/serverSettingsAtoms';
import { storagesStreamAtom } from './api/storagesAtoms';
import { suggestionsStreamAtom } from './api/suggestionsAtoms';
import { updatesStreamAtom } from './api/updatesAtoms';

type StreamHandler = WritableAtom<null, [StreamInput], void>;

/** One write atom that hands `input` to every handler in turn. */
export function streamInputAtomOf(handlers: readonly StreamHandler[]): StreamHandler {
    return atom(null, (_get, set, input: StreamInput): void => {
        for (const handler of handlers) {
            try {
                set(handler, input);
            } catch {
                // One resource failing must not keep the stream from the rest.
            }
        }
    });
}

export const streamInputAtom = streamInputAtomOf([
    agentSettingsStreamAtom,
    binaryFilesStreamAtom,
    devicesStreamAtom,
    healthMessagesStreamAtom,
    liveCamerasStreamAtom,
    liveStatusStreamAtom,
    locationsStreamAtom,
    meStreamAtom,
    rulesStreamAtom,
    serverSettingsStreamAtom,
    serversStreamAtom,
    storagesStreamAtom,
    suggestionsStreamAtom,
    updatesStreamAtom,
]);

/**
 * Hands each input in turn to every handler, all in one write. Jotai then computes what depends
 * on many cameras or resources once for the whole batch, not once for each input in it.
 */
export const streamInputsAtom = atom(null, (_get, set, inputs: readonly StreamInput[]): void => {
    for (const input of inputs) {
        set(streamInputAtom, input);
    }
});
